import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import type { User } from '../api/types';

type AuthCtx = {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string, remember: boolean, location?: any) => Promise<void>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
};

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User|null>(null);
  const [loading, setLoading] = useState(true);

  async function refreshMe() {
    try {
      const r = await api.get<{user: User}>('/api/me');
      setUser(r.user);
    } catch (e: any) {
      setUser(null);
    }
  }

  useEffect(() => {
    (async () => {
      await refreshMe();
      setLoading(false);
    })();
  }, []);

  async function login(username: string, password: string, remember: boolean, location?: any) {
    await api.post('/api/auth/login', { username, password, remember, location });
    await refreshMe();
  }

  async function logout() {
    await api.post('/api/auth/logout');
    setUser(null);
  }

  const value = useMemo(() => ({ user, loading, login, logout, refreshMe }), [user, loading]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth fuori da AuthProvider');
  return v;
}
